import * as React from 'react';
import {IMessage} from '../../Constants/Interfaces';
import {Ref} from '../../Constants';
import {err, getMessagesReference, isMyId, log, warn} from '../../Utils';
import classNames from 'classnames';

import './Thread.sass';
import {Icon} from '@iconify/react';
import {MessageData} from '../Message';
import {readMessage} from '../../Messages';


type MessagesMap = { [key: number]: IMessage };


interface Props {
    cid: string;
}


interface State {
    messages: MessagesMap;
    scrolledToBottom: boolean;
    scrolledToTop: boolean;
    noPreviousMessages: boolean;
}


abstract class ThreadData extends React.Component<Props, State> {
    public state: State = {
        messages: {},
        scrolledToBottom: false,
        scrolledToTop: false,
        noPreviousMessages: false,
    };

    limit: number = 25;
    pages: number = 1;


    public async componentDidMount() {
        log('Component did mount.');
        await this.loadInitialMessages();
        await this.attachListeners();
    }


    public async componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any) {
        log('Component did update.');
        if (prevProps.cid !== this.props.cid) {
            await this.handleCidChange(prevProps.cid);
        }
    }


    public componentWillUnmount() {
        ThreadData.detachListeners(this.props.cid);
    }


    private async handleCidChange(prevCid: string): Promise<void> {
        log('CID changed');
        ThreadData.detachListeners(prevCid);
        this.pages = 1;
        await this.loadInitialMessages();
        await this.attachListeners();
        //
        this.scrollToBottom()();
    }


    private async loadInitialMessages(): Promise<void> {
        log('Loading initial messages...');
        const {cid} = this.props;

        try {
            const Ref = getMessagesReference(cid);

            const messagesDataSnapshot = await Ref.limitToLast(this.limit).get();

            let messagesDataValue = messagesDataSnapshot.val() as MessagesMap | null;

            const noPreviousMessages = !messagesDataValue || Object.keys(messagesDataValue).length < this.limit;

            //@ts-ignore
            this.setState({messages: messagesDataValue || {}, noPreviousMessages});
        } catch (error) {
            err('Failed retrieving initial messages!', error.message);
        }
    }


    private async attachListeners(): Promise<void> {
        log('Attaching listeners...');
        const {cid} = this.props;
        const {messages} = this.state;

        try {
            const now = new Date().getTime().toString(),
                firstMessageKey = Object.keys(messages || {})[0],
                orderedMessagesRef = getMessagesReference(cid).orderByKey(),
                loadedMessagesRef = orderedMessagesRef.startAfter(firstMessageKey || now);

            //Listen for new messages
            orderedMessagesRef.startAfter(now).on('child_added', this.handleNewMessage);

            //Listen for loaded messages updates
            loadedMessagesRef.on('child_changed', this.handleMessageUpdate);

            //Listen for message deletion
            loadedMessagesRef.on('child_removed', this.handleMessageDelete);
        } catch (error) {
            err('Failed attaching listeners!', error.message);
        }
    }


    protected loadPreviousMessages = async (): Promise<void> => {
        //ToDo: actually load PREVIOUS messages
        log('Loading previous messages...');
        const {cid} = this.props;

        this.pages++;

        try {
            //If I set a query to start after first message in the list, onDelete listener starts freaking out.
            const Ref = getMessagesReference(cid)
                    .orderByKey()
                    .limitToLast(this.limit * this.pages),
                previousMessagesDataSnapshot = await Ref.get(),
                previousMessagesDataValue = previousMessagesDataSnapshot.val(),
                noPreviousMessages = !previousMessagesDataValue;

            if (noPreviousMessages) return;

            //@ts-ignore
            this.setState((state: State) => ({
                messages: {...previousMessagesDataValue, ...state.messages},
            }));
        } catch (error) {
            err('Failed retrieving previous messages!', error.message);
        }
    };

    private handleNewMessage = (snap: Ref): void => {
        warn('New message received!');

        const messageDataValue = snap.val() as IMessage;

        if (messageDataValue.unread) {
            readMessage(messageDataValue);
        }

        this.setState(
            (state: State) => ({
                //@ts-ignore | Handle key absence error.
                messages: {...state.messages, [snap.key]: messageDataValue},
            }),

            //Scroll to bottom if current user sent a message
            this.state.scrolledToBottom || isMyId(messageDataValue.sender) ? this.scrollToBottom('smooth') : undefined,
        );
    };

    private handleMessageUpdate = (snap: Ref): void => {
        warn('Message updated!');
        //@ts-ignore
        this.setState((state: State) => {
            const messages = Object.assign({}, state.messages);

            //@ts-ignore | Handle key absence error.
            messages[snap.key] = snap.val();
            return {messages};
        });
    };

    private handleMessageDelete = (snap: Ref): void => {
        warn('Message deleted!');
        //@ts-ignore
        this.setState((state: State) => {
            const messages = Object.assign({}, state.messages);

            //@ts-ignore | Handle key absence error.
            delete messages[snap.key];
            return {messages};
        });
    };


    private static detachListeners(prevCid: string): void {
        log('Listeners detached.');
        getMessagesReference(prevCid).off();
    }


    protected abstract scrollToBottom(behavior?: 'smooth' | 'auto'): () => void;
}


export class ThreadRender extends ThreadData {
    scrollRef = React.createRef<HTMLDivElement>();


    public render() {
        const {scrolledToTop, scrolledToBottom, noPreviousMessages} = this.state;
        return (
            <div className={classNames('thread')}>
                <div ref={this.scrollRef} className={classNames('messages')}>
                    {Object.entries(this.state.messages).map(([key, message]) => (
                        <MessageData key={key} message={message}/>
                    ))}
                </div>

                {scrolledToTop && !noPreviousMessages && (
                    <button
                        className={classNames('top-button')}
                        onClick={this.handleLoadNewMessages}
                        children={'load previous messages'}
                    />
                )}

                {!scrolledToBottom && (
                    <Icon
                        className={classNames('bottom-button')}
                        onClick={this.scrollToBottom('smooth')}
                        icon={'eva:arrow-ios-downward-fill'}
                    />
                )}
            </div>
        );
    }


    public async componentDidMount(): Promise<void> {
        await super.componentDidMount();

        this.scrollRef.current?.addEventListener<'scroll'>('scroll', this.handleScroll);

        this.scrollToBottom()();
    }


    private handleLoadNewMessages = async (): Promise<void> => {
        //Figure out how to handle this case.
        if (!this.scrollRef?.current) return;
        const scrollHeightBeforeFetch = this.scrollRef.current.scrollHeight;
        await this.loadPreviousMessages();
        const scrollHeightAfterFetch = this.scrollRef.current.scrollHeight;
        this.scrollToBottom('auto', scrollHeightAfterFetch - scrollHeightBeforeFetch)();
    };

    private handleScroll = (): void => {
        //Figure out how to handle this case.
        if (!this.scrollRef?.current) return;

        const {scrollHeight, scrollTop, clientHeight} = this.scrollRef.current as HTMLDivElement;

        if (scrollHeight <= clientHeight) {
            return this.setState({scrolledToTop: false, scrolledToBottom: true});
        }

        const scrolledToBottom: boolean = scrollTop + clientHeight > scrollHeight - 40,
            scrolledToTop = scrollTop === 0;

        if (scrolledToBottom && !this.state.scrolledToBottom) {
            this.setState({scrolledToBottom: true});
        }
        if (!scrolledToBottom && this.state.scrolledToBottom) {
            this.setState({scrolledToBottom: false});
        }
        if (scrolledToTop && !this.state.scrolledToTop) {
            this.setState({scrolledToTop: true});
        }
        if (!scrolledToTop && this.state.scrolledToTop) {
            this.setState({scrolledToTop: false});
        }
    };

    protected scrollToBottom =
        (behavior: 'smooth' | 'auto' = 'auto', top: number = this.scrollRef.current?.scrollHeight || 0) =>
            () => {
                this.scrollRef.current?.scroll({top, behavior});
            };
}
